"use strict";
// Copyright (c) 2021 by Move Closer
Object.defineProperty(exports, "__esModule", { value: true });
exports.useBuilder = exports.builderProps = void 0;
const composition_api_1 = require("@vue/composition-api");
const uuid_1 = require("uuid");
const Container_1 = require("../Container");
/**
 * @author Łukasz Sitnicki <lukasz.sitnicki@movecloser.pl>
 */
exports.builderProps = {
    breakpoint: {
        type: String,
        required: true
    },
    config: {
        type: Object,
        required: false,
        default: () => ({})
    },
    containerComponent: {
        type: [Function, Object],
        required: false
    },
    containers: {
        type: Object,
        required: true
    },
    elementProps: {
        type: Object,
        required: false,
        default: () => ({})
    },
    moduleComponent: {
        type: [Function, Object],
        required: true
    },
    modules: {
        type: Object,
        required: true
    },
    replicateContainer: {
        type: Function,
        required: false
    },
    replicateModules: {
        type: Function,
        required: false
    },
    structure: {
        type: Object,
        required: true
    }
};
/**
 * @author Łukasz Sitnicki <lukasz.sitnicki@movecloser.pl>
 */
function createDefaultBuilderConfig(canCreateContainer = false, canCreateModule = false, hasContainerComponent = false) {
    return {
        columns: Container_1.defaultColumnsCount,
        controls: {
            addContainer: true,
            addModule: true,
            cloneContainer: canCreateContainer,
            cloneModule: canCreateModule,
            copyContainer: false,
            copyModule: false,
            editContainer: hasContainerComponent,
            editModule: true,
            removeContainer: true,
            removeModule: true
        },
        minRowHeight: true,
        showControls: true
    };
}
/**
 * @author Łukasz Sitnicki <lukasz.sitnicki@movecloser.pl>
 */
exports.useBuilder = (props, ctx) => {
    const { breakpoint, config, structure } = composition_api_1.toRefs(props);
    const hoveredModule = composition_api_1.ref(null);
    const hoveredContainer = composition_api_1.ref(null);
    const selectedContainer = composition_api_1.ref(null);
    const selectedModule = composition_api_1.ref(null);
    const resolvedConfig = composition_api_1.computed(() => {
        return Object.assign(Object.assign({}, createDefaultBuilderConfig(typeof props.replicateContainer === 'function', typeof props.replicateModules === 'function', typeof props.containerComponent !== 'undefined')), config.value);
    });
    const id = composition_api_1.computed(() => (['b', `${uuid_1.v4().split('-').pop()}`].join('-')));
    const gridTemplate = composition_api_1.computed(() => {
        if (resolvedConfig.value.columns === Container_1.defaultColumnsCount) {
            return null;
        }
        return `#${id.value} .builder__container-inner { grid-template-columns: repeat(${resolvedConfig.value.columns}, minmax(0, 1fr)); }`;
    });
    const showAddContainerBtn = composition_api_1.computed(() => {
        return resolvedConfig.value.showControls && resolvedConfig.value.controls.addContainer;
    });
    const structureToDisplay = composition_api_1.computed({
        get: () => {
            return structure.value[breakpoint.value] || [];
        },
        set: (breakpointStructure) => {
            ctx.emit('update:structure', Object.assign(Object.assign({}, structure.value), { [breakpoint.value]: breakpointStructure }));
        }
    });
    function _cloneModules(definitions, modules) {
        if (typeof props.replicateModules !== 'function') {
            throw new Error('Builder: Cannot replicate module with prop [replicateModules] not defined.');
        }
        const newDefinitions = [];
        const newModules = props.replicateModules(modules);
        for (const m in newModules) {
            if (newModules[m].id === modules[m].id) {
                throw new Error(`Builder: Replicated module [${modules[m].id}] has same id as it's origin.`);
            }
            newDefinitions.push(Object.assign(Object.assign({}, JSON.parse(JSON.stringify(definitions[m]))), { module: newModules[m].id }));
        }
        return newDefinitions;
    }
    //
    // function _escapeKeyListener (e: KeyboardEvent): void {
    //   if (e.keyCode === 27) {
    //     selectedContainer.value = null
    //     selectedModule.value = null
    //   }
    // }
    function addContainer(destination) {
        ctx.emit('addContainer', { destination });
    }
    function addModule(container, index) {
        ctx.emit(Container_1.ContainerEventType.AddModule, { name: Container_1.ContainerEventType.AddModule, container, index });
    }
    function cloneContainer(definition, container) {
        const hasDefinedContainer = typeof props.containerComponent !== 'undefined';
        if (hasDefinedContainer && (!props.replicateContainer || !container)) {
            return;
        }
        const definitions = [...structureToDisplay.value];
        const indexOfDefinition = definitions.indexOf(definition);
        if (indexOfDefinition === -1) {
            ctx.emit('error', 'Builder: Cannot find container to clone.');
            return;
        }
        const moduleDefinitionIds = definition.modules.map(d => d.module);
        const modulesToClone = Object.values(props.modules)
            .filter(m => moduleDefinitionIds.includes(m.id));
        try {
            const clonedDefinitions = _cloneModules(definition.modules, modulesToClone);
            // eslint-disable-next-line  @typescript-eslint/ban-ts-ignore
            // @ts-ignore
            const newContainer = hasDefinedContainer ? props.replicateContainer(container) : {
                id: uuid_1.v4().toString()
            };
            if (container && newContainer.id === container.id) {
                throw new Error('Builder: Replicated container has same id as it\'s origin.');
            }
            definitions.splice(indexOfDefinition + 1, 0, {
                container: newContainer.id,
                modules: clonedDefinitions
            });
            structureToDisplay.value = definitions;
        }
        catch (e) {
            ctx.emit('error', e);
        }
    }
    function cloneModule(container, definition, module) {
        const definitions = [...structureToDisplay.value];
        const indexOfDefinition = definitions[container].modules.indexOf(definition);
        if (indexOfDefinition === -1) {
            ctx.emit('error', 'Builder: Cannot find module to clone.');
            return;
        }
        try {
            const clonedDefinitions = _cloneModules([definition], [module]);
            definitions[container].modules.splice(indexOfDefinition + 1, 0, clonedDefinitions[0]);
            structureToDisplay.value = definitions;
        }
        catch (e) {
            ctx.emit('error', e);
        }
    }
    function hoverContainer(id) {
        hoveredContainer.value = id;
    }
    function hoverModule(id) {
        hoveredModule.value = id;
        hoveredContainer.value = null;
    }
    function selectContainer(id) {
        if (!resolvedConfig.value.showControls) {
            return;
        }
        if (selectedContainer.value === id) {
            selectedContainer.value = null;
        }
        else {
            selectedContainer.value = id;
            selectedModule.value = null;
        }
    }
    function selectModule(id, force) {
        if (!resolvedConfig.value.showControls) {
            return;
        }
        if (force) {
            selectedModule.value = id;
            selectedContainer.value = null;
        }
        else {
            if (selectedModule.value === id) {
                selectedModule.value = null;
            }
            else {
                selectedModule.value = id;
                selectedContainer.value = null;
            }
        }
    }
    function updateModuleDefinition(containerIndex, definition) {
        const definitions = [...structureToDisplay.value];
        const indexOfDefinition = definitions[containerIndex].modules.findIndex(d => d.module === definition.module);
        if (indexOfDefinition === -1) {
            ctx.emit('error', 'Builder: Cannot find module definition to update.');
            return;
        }
        definitions[containerIndex].modules[indexOfDefinition] = definition;
        structureToDisplay.value = definitions;
    }
    // onMounted(() => {
    //   document.addEventListener('keyup', _escapeKeyListener)
    // })
    //
    // onUnmounted(() => {
    //   document.removeEventListener('keyup', _escapeKeyListener)
    // })
    return {
        addContainer,
        addModule,
        cloneContainer,
        cloneModule,
        gridTemplate,
        hoverContainer,
        hoveredContainer,
        hoverModule,
        hoveredModule,
        id,
        resolvedConfig,
        selectContainer,
        selectedContainer,
        selectModule,
        selectedModule,
        showAddContainerBtn,
        structureToDisplay,
        updateModuleDefinition
    };
};
//# sourceMappingURL=Builder.hooks.js.map